import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, List, ListItem, ListItemText, TextField, Typography,
} from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import { PILE_CHASSE_FIXE } from '../../theme/jetons';

/**
 * La suppression d'une entreprise — le geste le plus destructeur du produit.
 *
 * 🔴 POURQUOI UN DIALOGUE À PART, ET PAS `DialogueConfirmation`. Celui-ci porte
 * une SAISIE, et une saisie change la nature de la confirmation : on ne
 * demande plus « êtes-vous sûr », on demande de DÉSIGNER la cible. *Un bouton
 * se clique par réflexe ; un nom se retape en regardant ce qu'on a
 * sélectionné.* Ajouter un champ au dialogue commun lui aurait appris un cas
 * — exactement ce qu'un composant partagé doit refuser (lot C).
 *
 * ── L'ANNONCE VIENT DU SERVEUR, JAMAIS D'UN DÉCOMPTE LOCAL ─────────────────
 * `impact` est le corps de `GET /admin/companies/<id>/impact-suppression`.
 * Recompter ici serait une SECONDE écriture de la règle, qui divergerait de la
 * suppression au premier changement de schéma — le défaut A du lot 6d
 * transposé à une annonce. *Une annonce calculée autrement que l'action
 * qu'elle annonce est une annonce qui peut mentir.*
 *
 * ── ⚠️ ELLE DIT AUSSI CE QUI SURVIT ───────────────────────────────────────
 * Les journaux d'impersonation sont CONSERVÉS. *Une annonce qui n'énumère que
 * les pertes laisse croire que tout part* — et celui qui supprime doit savoir
 * ce qu'il ne supprime pas.
 */
/**
 * Un poids lisible : « 812 Ko », « 3,4 Mo ». L'unité SUIT la valeur.
 *
 * ⚠️ Même leçon que le message du 413 : une division entière en mégaoctets
 * annonce « 0 Mo » sous le seuil du mégaoctet — *un poids affiché à zéro dit
 * qu'il n'y a rien à perdre.*
 */
const formaterOctets = (n) => {
  if (!n) return '0 Ko';
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
};

const DialogueSuppressionEntreprise = ({
  ouvert, impact, chargement = false, enCours = false, erreur = null,
  onConfirmer, onFermer,
}) => {
  const { t } = useLanguage();
  const [saisie, setSaisie] = useState('');

  const nom = impact?.company_name || '';
  const conserve = impact?.conserve || {};
  const detruitOctets = impact?.detruit?.fichiers_octets ?? 0;

  // ⚠️ MÊME RÈGLE QUE LE SERVEUR : espaces de bord tolérés, casse NON.
  // Elle est écrite des deux côtés — et c'est assumé, parce que les deux
  // destinataires diffèrent : ici elle ACTIVE un bouton, là-bas elle REFUSE
  // une requête. Le serveur reste seul juge ; celle-ci n'est qu'un confort.
  // *Un contrôle côté client est une commodité, jamais une garantie.*
  const correspond = saisie.trim() === nom && nom !== '';

  // ⚠️ `impact?.detruit` EST DÉPLIÉ DANS LE CALLBACK, pas au-dessus. Un
  // `const detruit = impact?.detruit || {}` fabrique un objet NEUF à chaque
  // rendu : la dépendance change toujours, et le `useMemo` ne mémoïse plus
  // rien — il coûte sans servir. C'est ce que `react-hooks/exhaustive-deps`
  // signalait, et il avait raison. *Un repli `|| {}` posé hors d'un hook
  // devient une dépendance instable.*
  const lignes = useMemo(() => {
    const d = impact?.detruit || {};
    return [
      ['utilisateurs', d.utilisateurs], ['stocks', d.stocks],
      ['produits', d.produits], ['fournisseurs', d.fournisseurs],
      ['commandes', d.commandes], ['receptions', d.receptions],
      ['paiements', d.paiements], ['invitations', d.invitations],
      ['fichiers', d.fichiers],
    ];
  }, [impact]);

  const fermer = () => { setSaisie(''); onFermer(); };

  return (
    <Dialog open={Boolean(ouvert)} onClose={enCours ? undefined : fermer}
            maxWidth="sm" fullWidth>
      <DialogTitle>{t('superAdmin.deleteTitle')}</DialogTitle>
      <DialogContent>
        {chargement && (
          <Typography variant="body2">{t('common.loading')}</Typography>
        )}

        {!chargement && impact && (
          <>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {t('superAdmin.deleteQuestion', { name: nom })}
            </Typography>

            {/* ── CE QUI SERA DÉTRUIT, NOMMÉ LIGNE PAR LIGNE ──────────────
                🔴 PAS « êtes-vous sûr ». Un décompte se lit et décide ;
                une question fermée ne fait que demander un clic de plus. */}
            <Typography variant="overline" color="text.secondary">
              {t('superAdmin.deleteWillDestroy')}
            </Typography>
            <List dense disablePadding sx={{ mb: 1 }}>
              {lignes.filter(([, n]) => n > 0).map(([cle, n]) => (
                <ListItem key={cle} disableGutters sx={{ py: 0 }}>
                  <ListItemText
                    primaryTypographyProps={{ variant: 'body2' }}
                    /* ⚠️ LES FICHIERS PORTENT LEUR POIDS, les autres non.
                       « 4 fichiers » ne dit pas si l'on perd quatre vignettes
                       ou quatre PDF de bons de commande — *un décompte sans
                       ordre de grandeur laisse imaginer le plus petit.* Le
                       serveur calcule déjà `fichiers_octets` ; il ne manquait
                       que de l'afficher. */
                    primary={cle === 'fichiers'
                      ? t('superAdmin.impact_fichiers_poids', {
                          count: n, poids: formaterOctets(detruitOctets),
                        })
                      : `${n} ${t(`superAdmin.impact_${cle}`)}`}
                  />
                </ListItem>
              ))}
              {/* Une entreprise vide n'a rien à énumérer : on le DIT, plutôt
                  que d'afficher une liste vide qui ressemble à un chargement. */}
              {lignes.every(([, n]) => !n) && (
                <ListItem disableGutters sx={{ py: 0 }}>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }}
                                primary={t('superAdmin.impactVide')} />
                </ListItem>
              )}
            </List>

            {/* ── ⚠️ CE QUI SURVIT, DIT DANS LES DEUX CAS ────────────────
                🔴 L'ALERTE ÉTAIT CONDITIONNÉE À `> 0` : sur une entreprise
                sans trace d'impersonation — le cas ORDINAIRE d'une société
                d'essai — elle disparaissait, et l'annonce ne nommait plus que
                des pertes. *Une annonce qui n'énumère que ce qui part laisse
                croire que tout part.*
                ⚠️ Et ce n'est pas un décompte qu'il faut annoncer, c'est une
                RÈGLE : le journal d'audit n'est jamais supprimé. Elle vaut
                que le compteur soit à zéro ou à douze. */}
            <Alert severity="info" sx={{ mb: 2 }}>
              {conserve.journaux_impersonation > 0
                ? t('superAdmin.deleteSurvives', {
                    count: conserve.journaux_impersonation,
                  })
                : t('superAdmin.deleteSurvives_zero')}
            </Alert>

            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('superAdmin.deleteWarning')}
            </Alert>

            <Divider sx={{ mb: 2 }} />

            {/* ── LA CONFIRMATION FORTE ───────────────────────────────────
                ⚠️ LE NOM EST AFFICHÉ À CÔTÉ DU CHAMP. Devoir revenir à la
                liste pour le lire ferait recopier de mémoire — et une
                mémoire approximative sur un geste irréversible est
                exactement ce qu'on cherche à empêcher. */}
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('superAdmin.deleteTypeName')}
            </Typography>
            <Box sx={{
              mb: 1, px: 1.5, py: 1, borderRadius: 1,
              bgcolor: 'action.hover',
              // ⚠️ `PILE_CHASSE_FIXE`, JAMAIS une pile écrite à la main.
              // La garde L3 de `test_theme` m'a repris ici : elle a été écrite
              // pour trois recopies portant DEUX valeurs différentes, et elle a
              // attrapé une quatrième, neuve. *Une garde qui énonce une propriété
              // défend du code qui n'existait pas quand elle a été écrite.*
              fontFamily: PILE_CHASSE_FIXE,
              fontSize: 14, userSelect: 'all',
            }}>
              {nom}
            </Box>
            <TextField
              fullWidth autoFocus size="small"
              value={saisie}
              onChange={(e) => setSaisie(e.target.value)}
              disabled={enCours}
              placeholder={nom}
              inputProps={{
                // ⚠️ La correction automatique du navigateur réécrirait une
                // raison sociale — et un nom corrigé ne correspond plus.
                autoCapitalize: 'off', autoCorrect: 'off', spellCheck: 'false',
              }}
              error={saisie.length > 0 && !correspond}
              helperText={saisie.length > 0 && !correspond
                ? t('superAdmin.deleteNameMismatch') : ' '}
            />
          </>
        )}

        {erreur && <Alert severity="error" sx={{ mt: 1 }}>{erreur}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={fermer} disabled={enCours}>
          {t('common.cancel')}
        </Button>
        {/* 🔴 DÉSACTIVÉ TANT QUE LE NOM NE CORRESPOND PAS. Le bouton ne peut
            pas être atteint par réflexe : il faut avoir écrit la cible. */}
        <Button variant="contained" color="error" disabled={!correspond || enCours}
                onClick={() => onConfirmer(saisie.trim())}>
          {t('superAdmin.deleteConfirmLabel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogueSuppressionEntreprise;
